import React from 'react';
import Head from 'next/head';
import I18N from '@/services/I18N';
import { setAppLocale } from '@/store/slices/app';
import { getISOLocale } from '@/models/locale';
import useAllCoaches from '@/hooks/allCoaches';
import Text from '../../../../components/app/Text';
import LayoutWithNav from '../../../../layouts/LayoutWithNav';
import ViewAllList from '../../../../components/content/ViewAllList';
import Loader from '../../../../components/app/Loader';
import { wrapper } from '../../../../store';
import routeConstants from '../../../../utils/constants/routes';
import {
  getMeditation,
  getMeditationIdFromSlug,
} from '../../../../models/meditation';
import AuraContent from '../../../../services/AuraContent';
import { getCoach } from '../../../../models/coach';
import urlFormater from '../../../../utils/urlFormater';

async function fetchPageData(query, locale) {
  if (!query[routeConstants.SLUG_MEDITATION]) {
    return {
      error: 'track slug not found',
    };
  }
  let trackId = await getMeditationIdFromSlug(
    query[routeConstants.SLUG_MEDITATION]
  );
  if (!trackId) {
    trackId = urlFormater.getIDFromURL(query[routeConstants.SLUG_MEDITATION]);
  }
  if (!trackId) {
    return {
      error: 'trackID not found',
    };
  }
  const track = await getMeditation(trackId);
  if (!track) {
    return {
      error: 'track not found',
    };
  }
  const trackTitle = track.title;
  const relatedTracks = await AuraContent.getTracks({
    routeSuffix: `contents/${track.session}/related`,
    overrideDurationFilter: 4,
    overrideContentType: `all`,
    limit: 50,
    locale: 'all',
    preferredLocale: locale,
  });
  const coach = await getCoach(track.userId);

  return {
    relatedTracks,
    slugMeditation: track.slug,
    coach,
    trackTitle,
  };
}

function ViewAllRelatedTracks(serverProps) {
  const { relatedTracks, slugMeditation, coach, trackTitle } = serverProps;
  useAllCoaches();
  return (
    <LayoutWithNav showSEOFooter>
      <Head>
        <title>{`Tracks similar to ${trackTitle} by ${
          coach ? coach.name : ''
        } - Aura`}</title>
        <meta
          name="description"
          content={`Explore tracks similar to ${trackTitle} by ${
            coach ? coach.name : ''
          } and much more on Aura, the world's best app for guided meditations, stories, and life coaching for better sleep, lower stress, and less anxiety.`}
        />
        <meta
          property="og:title"
          content={`Tracks similar to ${trackTitle} by ${
            coach ? coach.name : ''
          }  - Aura`}
        />
        <meta
          property="og:description"
          content={`Explore tracks similar to ${trackTitle} by ${
            coach ? coach.name : ''
          } and much more on Aura, the world's best app for guided meditations, stories, and life coaching for better sleep, lower stress, and less anxiety.`}
        />
        <meta
          itemProp="image"
          property="og:image"
          content={`${process.env.NEXT_PUBLIC_APP_DOMAIN}/static/images/banner.jpg`}
        />
        <meta
          property="og:url"
          content={`${process.env.NEXT_PUBLIC_APP_DOMAIN}/${routeConstants.PAGE_TRACK}/${slugMeditation}/related`}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_APP_DOMAIN}/${routeConstants.PAGE_TRACK}/${slugMeditation}`}
        />
      </Head>
      <div className="page">
        {!relatedTracks ? (
          <Loader />
        ) : (
          <div className="content-padding">
            <Text
              type="h2"
              component="h1"
              color="b100"
              style={{ marginBottom: 24 }}>
              {`Tracks similar to ${trackTitle}`}
            </Text>
            <ViewAllList data={relatedTracks} label={'tracks'} />
          </div>
        )}
      </div>
    </LayoutWithNav>
  );
}
export const getServerSideProps = wrapper.getServerSideProps(
  (store) =>
    async ({ query, locale }) => {
      let props = await fetchPageData(query, getISOLocale(locale));

      props.key = query[routeConstants.SLUG_MEDITATION];
      if (props.error) {
        return { notFound: true };
      }
      await store.dispatch(setAppLocale(getISOLocale(locale)));

      props = {
        ...props,
        ...(await I18N.loadLocale({
          locale,
          route: '/track/[slugMeditation]/similar-tracks',
        })),
      };
      return { props };
    }
);

export default ViewAllRelatedTracks;
